/**
 * useVoiceInput - Audio recording + AI transcription hook
 *
 * Uses MediaRecorder API to capture audio, then sends to Gemini
 * for transcription with grammar cleanup. Designed for field
 * technicians dictating notes on mobile devices.
 *
 * @example
 * ```tsx
 * const voice = useVoiceInput({
 *   fieldContext: 'Visit Summary',
 *   onTranscription: (text) => setNotes(prev => prev + '\n' + text),
 * });
 *
 * <button onClick={voice.state === 'recording' ? voice.stopRecording : voice.startRecording}>
 *   {voice.state === 'recording' ? 'Stop' : 'Record'}
 * </button>
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { TimeoutError } from '../../lib/utils/fetchWithTimeout';
import { fetchJsonWithRetry, describeFetchFailure } from '../../lib/utils/transientError';
import { usePublicFormContext } from '../../components/forms/public/PublicFormContext';

const API_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-quoter-saas.cloudfunctions.net';

export type VoiceState = 'idle' | 'recording' | 'transcribing' | 'error';

interface UseVoiceInputOptions {
  /** Context for the AI transcriber (e.g., "Visit Summary", "Reported Problem") */
  fieldContext?: string;
  /** Called with transcribed text on success */
  onTranscription: (text: string) => void;
}

interface UseVoiceInputReturn {
  state: VoiceState;
  error: string | null;
  /** Recording duration in seconds */
  recordingDuration: number;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  /** Whether browser supports MediaRecorder + getUserMedia */
  isSupported: boolean;
}

/**
 * Determine the best supported audio MIME type.
 * WebM is preferred (smaller files), MP4 as fallback for iOS Safari.
 */
function getSupportedMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';

  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }

  return 'audio/webm'; // fallback
}

export function useVoiceInput({
  fieldContext,
  onTranscription,
}: UseVoiceInputOptions): UseVoiceInputReturn {
  const [state, setState] = useState<VoiceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const { getAiCredential } = usePublicFormContext();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const isSupported =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof MediaRecorder !== 'undefined';

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Voice recording not supported in this browser');
      setState('error');
      return;
    }

    try {
      setError(null);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        // Stop the timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Stop microphone access
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        // Build the audio blob
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];

        if (blob.size === 0) {
          setError('No audio captured');
          setState('error');
          setTimeout(() => setState('idle'), 3000);
          return;
        }

        // Convert to base64
        setState('transcribing');
        try {
          const base64 = await blobToBase64(blob);
          const baseMime = mimeType.split(';')[0]; // Strip codec params

          // Retries transient failures (cold start, timeout, 429/502/503/504) automatically
          // before surfacing anything to the user — see #2360.
          const data = await fetchJsonWithRetry<{ success?: boolean; text?: string; error?: string }>(
            `${API_BASE_URL}/forms_transcribeAudio`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(await getAiCredential()) },
              body: JSON.stringify({
                audioData: base64,
                mimeType: baseMime,
                fieldContext,
              }),
              timeout: 30000, // 30 seconds for transcription
              // A 429 here means the AI quota window is spent; retrying would
              // charge it again and keep the window pinned.
              retryOn429: false,
            },
          );

          if (data.success && data.text) {
            onTranscription(data.text);
            setState('idle');
            setRecordingDuration(0);
          } else {
            setError(data.error || 'Transcription failed');
            setState('error');
            setTimeout(() => setState('idle'), 4000);
          }
        } catch (err) {
          // describeFetchFailure distinguishes a cold-start-adjacent transient failure
          // (already retried by fetchJsonWithRetry) from a real connectivity problem —
          // see #2360.
          const message = err instanceof TimeoutError
            ? 'Transcription timeout — audio may be too long'
            : describeFetchFailure(err);
          setError(message);
          setState('error');
          setTimeout(() => setState('idle'), 4000);
        }
      };

      // Start recording with 1-second chunks
      recorder.start(1000);
      startTimeRef.current = Date.now();
      setState('recording');
      setRecordingDuration(0);

      // Duration timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (err) {
      // Clean up mic stream if it was acquired before the error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const msg =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access denied'
          : 'Could not start recording';
      setError(msg);
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  }, [isSupported, fieldContext, onTranscription, getAiCredential]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  return {
    state,
    error,
    recordingDuration,
    startRecording,
    stopRecording,
    isSupported,
  };
}

/**
 * Convert a Blob to a base64 string (without the data:...;base64, prefix).
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the data URL prefix: "data:audio/webm;base64,"
      const base64 = result.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
